import { useContext, useMemo } from 'react';
import { matchPath, useLocation, useParams } from '@bbkShared/react-router';
import { useSelector } from 'react-redux';
import { produce } from 'immer';
import { EditOfferPageContext } from '@bbkAdminComponents/experiences/offers/edit-offer-page-context';
import { collectForwards } from '@bbkAdminComponents/experiences/offers/collect-forwards';
import { AuthorizedPaths, BASE_PATH } from '@bbkAdminRouter/router-paths';
import { collectChainOfCards } from '@bbkAdminComponents/experiences/offers/edit-offer-utils';
import { selectApp, selectAppId } from '@bbkAdminRedux/app/selectors';
import { useSelectBillingIntegration } from '@bbkAdminRedux/rtkq/integrations.slice';
import { CtaProcessingOption } from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { type SelectOption } from '@bbkAdminComponents/select/select';
import { type RestCard } from '@bbkAdminUtils/api-client/rest-experiences-api';
import {
  OfferCategory,
  OfferObjective,
  OfferPresentation,
} from '@bbkAdminUtils/api-client/rest-offers-api';
import { type PatchPath } from '@bbkAdminUtils/utility-types';
import { IntegrationDataType } from '@bbkAdminRedux/data-management/reducers';

export type SidebarState = {
  type:
    | 'ai-toplevel'
    | 'design'
    | 'activities'
    | 'items'
    | 'button'
    | 'state'
    | 'card'
    | 'placement'
    | 'branding'
    | 'position';
  path: PatchPath;
};
export const useParseUrlToSidebarData = (): SidebarState | undefined => {
  const location = useLocation();
  const { ctaIdx } = useParams<{ offerId?: string; ctaIdx?: string }>();
  const { offer } = useContext(EditOfferPageContext);

  return useMemo(() => {
    const primaryCard = offer?.cards.find(
      (c) => c.name === offer.primary_card_name
    );
    if (!primaryCard) return undefined;
    const ctas = collectForwards(primaryCard);
    let ctaPath: PatchPath = [];
    if (ctaIdx) {
      const ctaIdxN = parseInt(ctaIdx, 10);
      const cta = ctas[ctaIdxN] as { path: PatchPath } | undefined;
      ctaPath = cta?.path ?? [];
    }
    switch (true) {
      case !!matchPath(
        location.pathname,
        `${BASE_PATH}${AuthorizedPaths.offersEditAITopLevel}`
      ): {
        return {
          type: 'ai-toplevel',
          path: [],
        };
      }
      case !!matchPath(
        location.pathname,
        `${BASE_PATH}${AuthorizedPaths.offersEditDesign}`
      ): {
        return {
          type: 'design',
          path: [],
        };
      }
      case !!matchPath(
        location.pathname,
        `${BASE_PATH}${AuthorizedPaths.offersEditActivities}`
      ): {
        return {
          type: 'activities',
          path: [],
        };
      }
      case !!matchPath(
        location.pathname,
        `${BASE_PATH}${AuthorizedPaths.offersEditItems}`
      ): {
        return {
          type: 'items',
          path: [],
        };
      }
      case !!matchPath(
        location.pathname,
        `${BASE_PATH}${AuthorizedPaths.offersEditSettings}`
      ): {
        return {
          type: 'card',
          path: ['cards', 0],
        };
      }
      case !!matchPath(
        location.pathname,
        `${BASE_PATH}${AuthorizedPaths.offersEditCtaSettings}`
      ): {
        return {
          type: 'button',
          path: ['cards', 0, ...ctaPath],
        };
      }
      case !!matchPath(
        location.pathname,
        `${BASE_PATH}${AuthorizedPaths.offersEditCtaState}`
      ): {
        return {
          type: 'state',
          path: ['cards', 0, ...ctaPath],
        };
      }
      case !!matchPath(
        location.pathname,
        `${BASE_PATH}${AuthorizedPaths.offersPlacement}`
      ): {
        return {
          type: 'placement',
          path: [],
        };
      }
      case !!matchPath(
        location.pathname,
        `${BASE_PATH}${AuthorizedPaths.offersEditBranding}`
      ): {
        return {
          type: 'branding',
          path: ['cards', 0, 'style_overlay'],
        };
      }
      case !!matchPath(
        location.pathname,
        `${BASE_PATH}${AuthorizedPaths.offerPosition}`
      ): {
        return {
          type: 'position',
          path: ['cards', 0, 'additional_properties', 'position'],
        };
      }
      default: {
        return undefined;
      }
    }
  }, [location, offer, ctaIdx]);
};
export const useGetChainOfCards = (cardNameForChain?: string): RestCard[] => {
  const { offer } = useContext(EditOfferPageContext);

  return useMemo(
    () =>
      cardNameForChain
        ? collectChainOfCards(offer, cardNameForChain)
            .map((cName) => offer.cards.find((c) => c.name === cName))
            .filter(Boolean)
        : offer.cards,
    [cardNameForChain, offer]
  );
};

export const CATEGORIES_DONT_ALLOW_BILLING_PROCESSING: OfferCategory[] = [
  OfferCategory.customer_feedback,
  OfferCategory.support,
];
const processingOpts: SelectOption<CtaProcessingOption>[] = [
  {
    value: CtaProcessingOption.url,
    label: 'Url redirect',
  },
  {
    value: CtaProcessingOption.email,
    label: 'Email',
  },
  {
    value: CtaProcessingOption.billing,
    label: 'Billing integration',
    isDisabled: true,
  },
  {
    value: CtaProcessingOption.webhook,
    label: 'Webhook',
  },
  {
    value: CtaProcessingOption.chargebee_checkout,
    label: 'Chargebee checkout',
  },
];
export const useProcessingOpts = (): SelectOption<CtaProcessingOption>[] => {
  const appKey = useSelector(selectAppId);
  const app = useSelector(selectApp);
  const isPCV2 = app.cb_product_catalog_version === 'v2';
  const billingIntegration = useSelectBillingIntegration({ appKey });

  const offerContext = useContext(EditOfferPageContext);

  const filterOfferProcessingOptions = (o: CtaProcessingOption): boolean => {
    const offerPresentation = offerContext?.offer.presentation;
    const offerObjective = offerContext?.offer.objective;
    if (o === CtaProcessingOption.chargebee_checkout) {
      return !!(
        billingIntegration &&
        billingIntegration.data_type === IntegrationDataType.CHARGEBEE &&
        offerObjective === OfferObjective.acquisition &&
        isPCV2
      );
    }
    if (
      !offerPresentation ||
      offerObjective === OfferObjective.churn_deflection ||
      offerObjective === OfferObjective.retention
    )
      return true;
    if (offerPresentation === OfferPresentation.banner) {
      return o === CtaProcessingOption.url || o === CtaProcessingOption.billing;
    } else if (
      offerPresentation === OfferPresentation.modal ||
      offerPresentation === OfferPresentation.pop_up
    ) {
      return o === CtaProcessingOption.url || o === CtaProcessingOption.billing;
    }
    return true;
  };

  return useMemo(() => {
    if (billingIntegration) {
      return produce(processingOpts, (draft) => {
        const draftBillingOpt = draft.find(
          (po) => po.value === CtaProcessingOption.billing
        );
        if (draftBillingOpt) {
          draftBillingOpt.isDisabled = false;
        }
      }).filter((opt) => filterOfferProcessingOptions(opt.value));
    }
    return processingOpts.filter((opt) =>
      filterOfferProcessingOptions(opt.value)
    );
  }, [billingIntegration, offerContext]);
};
